import { MobilePageShell } from '@/components/app/mobile-page-shell';
import { useNavigate } from '@tanstack/react-router';
import {
  addDays,
  endOfWeek,
  format,
  isSameDay,
  startOfWeek,
  subDays,
} from 'date-fns';
import { CalendarOff, ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { MobileHeaderIconButton } from '@/features/mobile-dashboard-header';
import {
  MOBILE_FILTER_CHROME_CLASS,
  MobileRecordList,
  MobileRecordListEmpty,
  MobileRecordListError,
  MobileRecordListLabel,
  MobileRecordListSkeleton,
  MobileRecordRow,
} from '@/features/mobile-ui';
import { useListLocations } from '@/features/organization-locations';
import { useListPractitioners } from '@/features/practitioners';
import {
  type ResolvedShiftDay,
  ShiftOverrideDialog,
  TimeOffDialog,
  closedLabel,
  formatHoursLabel,
  formatMinutesLabel,
  useClosedDays,
  useListShifts,
} from '@/features/scheduling';
import { cn } from '@/lib/utils';

interface OverrideTarget {
  practitionerId: string;
  practitionerName: string;
  date: string;
  resolvedDay: ResolvedShiftDay | null;
}

/** Total worked minutes across a resolved day's intervals. */
function dayMinutes(resolved: ResolvedShiftDay | null): number {
  if (!resolved || resolved.isOff) return 0;
  return resolved.intervals.reduce(
    (sum, i) => sum + (i.endMinutes - i.startMinutes),
    0
  );
}

function shiftLabel(resolved: ResolvedShiftDay | null): string {
  if (!resolved || resolved.isOff || resolved.intervals.length === 0) {
    return 'Not working';
  }
  return resolved.intervals
    .map(
      (interval) =>
        `${formatMinutesLabel(interval.startMinutes)} – ${formatMinutesLabel(
          interval.endMinutes
        )}`
    )
    .join(', ');
}

/**
 * Phone view of the weekly shifts grid. The 8-column roster cannot shrink to
 * 375px, so mobile shows one day at a time: a weekday selector plus a list of
 * every team member's shifts for that day. Tapping a row opens the same
 * per-day shift editor the desktop grid uses.
 */
export function TeamShiftsMobile({ locationSlug }: { locationSlug: string }) {
  const navigate = useNavigate();
  const { locations } = useListLocations();
  const location = locations.find(
    (item) => item.slug === locationSlug || item.id === locationSlug
  );
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [overrideTarget, setOverrideTarget] = useState<OverrideTarget | null>(
    null
  );
  const [timeOffOpen, setTimeOffOpen] = useState(false);

  const weekStart = useMemo(() => startOfWeek(selectedDate), [selectedDate]);
  const weekEnd = useMemo(() => endOfWeek(selectedDate), [selectedDate]);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const selectedKey = format(selectedDate, 'yyyy-MM-dd');
  const isToday = isSameDay(selectedDate, new Date());

  const {
    practitioners,
    isLoading: isPractitionersLoading,
    isError: isPractitionersError,
    error: practitionersError,
  } = useListPractitioners({ params: { isActive: true } });

  const {
    shiftDays,
    isLoading: isShiftsLoading,
    isError: isShiftsError,
  } = useListShifts({
    from: weekStart.toISOString(),
    to: weekEnd.toISOString(),
    locationId: location?.id,
  });

  const closed = useClosedDays(weekDays);
  const closedReason = closed.get(selectedKey);

  const isLoading = isPractitionersLoading || isShiftsLoading;
  const isError = isPractitionersError || isShiftsError;

  // Lookup: `${practitionerId}|${yyyy-MM-dd}` -> resolved day.
  const shiftLookup = useMemo(() => {
    const map = new Map<string, ResolvedShiftDay>();
    for (const day of shiftDays) {
      map.set(`${day.practitionerId}|${day.date}`, day);
    }
    return map;
  }, [shiftDays]);

  const resolvedFor = (practitionerId: string): ResolvedShiftDay | null =>
    shiftLookup.get(`${practitionerId}|${selectedKey}`) ?? null;

  // A closed day is nobody's working time, whatever the rota says.
  const dayTotalMinutes =
    closedReason === undefined
      ? practitioners.reduce(
          (sum, practitioner) => sum + dayMinutes(resolvedFor(practitioner.id)),
          0
        )
      : 0;

  return (
    <>
      <title>Shifts | Borrah</title>
      <MobilePageShell
        action={
          <MobileHeaderIconButton
            aria-label="Add time off"
            onClick={() => setTimeOffOpen(true)}
          >
            <CalendarOff className="size-5" />
          </MobileHeaderIconButton>
        }
        contentClassName="px-4 pb-4"
        title="Shifts"
      >
        {/* Day selector: step a day at a time, or jump within the week. */}
        <div className={cn(MOBILE_FILTER_CHROME_CLASS, 'rounded-2xl p-2')}>
          <div className="flex items-center justify-between gap-2 px-1 pt-1 pb-3">
            <time
              dateTime={format(selectedDate, 'yyyy-MM')}
              className="min-w-0 text-sm font-semibold whitespace-nowrap"
            >
              {format(selectedDate, 'MMMM yyyy')}
            </time>
            <div className="flex shrink-0 items-center gap-0.5">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Previous day"
                className="size-9 rounded-full"
                onClick={() => setSelectedDate((d) => subDays(d, 1))}
              >
                <ChevronLeft />
              </Button>
              {!isToday && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 rounded-full px-2"
                  onClick={() => setSelectedDate(new Date())}
                >
                  Today
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                aria-label="Next day"
                className="size-9 rounded-full"
                onClick={() => setSelectedDate((d) => addDays(d, 1))}
              >
                <ChevronRight />
              </Button>
            </div>
          </div>
          <ToggleGroup
            type="single"
            spacing={1}
            aria-label="Select day"
            className="grid w-full grid-cols-7 gap-1"
            value={selectedKey}
            onValueChange={(value) => {
              if (value) setSelectedDate(new Date(`${value}T12:00:00`));
            }}
          >
            {weekDays.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const selected = key === selectedKey;
              const today = isSameDay(day, new Date());
              return (
                <ToggleGroupItem
                  key={key}
                  value={key}
                  aria-label={format(day, 'EEEE d MMMM')}
                  aria-current={today ? 'date' : undefined}
                  className="h-auto flex-col gap-1 rounded-xl px-0 py-2"
                >
                  <span className="text-[10px] font-medium uppercase text-muted-foreground">
                    {format(day, 'EEE')}
                  </span>
                  <span
                    className={cn(
                      'flex size-7 items-center justify-center rounded-full text-sm font-semibold tabular-nums',
                      selected && 'bg-primary text-primary-foreground',
                      today && !selected && 'text-primary'
                    )}
                  >
                    {format(day, 'd')}
                  </span>
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
        </div>

        {isLoading ? (
          <MobileRecordListSkeleton />
        ) : isError ? (
          <MobileRecordListError
            message={`Failed to load shifts: ${
              practitionersError?.message || 'Unknown error'
            }`}
          />
        ) : practitioners.length === 0 ? (
          <MobileRecordListEmpty
            icon={Users}
            title="No team members yet"
            description="Add team members to plan their shifts."
          />
        ) : (
          <>
            <MobileRecordListLabel>
              {formatHoursLabel(dayTotalMinutes)} scheduled
            </MobileRecordListLabel>
            <MobileRecordList>
              {practitioners.map((practitioner) => {
                const resolved = resolvedFor(practitioner.id);
                const minutes =
                  closedReason === undefined ? dayMinutes(resolved) : 0;
                return (
                  <MobileRecordRow
                    key={practitioner.id}
                    testId={`shift-row-${practitioner.id}`}
                    title={practitioner.name}
                    subtitle={
                      closedReason === undefined
                        ? shiftLabel(resolved)
                        : closedLabel(closedReason)
                    }
                    trailing={
                      minutes > 0 ? (
                        <span className="text-[13px] font-medium tabular-nums">
                          {formatHoursLabel(minutes)}
                        </span>
                      ) : undefined
                    }
                    onClick={() =>
                      closedReason === undefined
                        ? setOverrideTarget({
                            practitionerId: practitioner.id,
                            practitionerName: practitioner.name,
                            date: selectedKey,
                            resolvedDay: resolved,
                          })
                        : navigate({
                            to: '/dashboard/l/$locationId/calendar',
                            params: { locationId: locationSlug },
                          })
                    }
                  />
                );
              })}
            </MobileRecordList>
          </>
        )}
      </MobilePageShell>

      {overrideTarget && location && (
        <ShiftOverrideDialog
          open
          onOpenChange={(open) => {
            if (!open) setOverrideTarget(null);
          }}
          practitionerId={overrideTarget.practitionerId}
          practitionerName={overrideTarget.practitionerName}
          date={overrideTarget.date}
          resolvedDay={overrideTarget.resolvedDay}
          locationId={location.id}
        />
      )}

      <TimeOffDialog open={timeOffOpen} onOpenChange={setTimeOffOpen} />
    </>
  );
}
